import { useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

// Non-blocking "new version available" banner for the installed PWA (registerType:
// 'prompt') — ported from the inniva-salesman-pwa pattern.
//
//  • useRegisterSW() registers the service worker and flips `needRefresh` when a freshly
//    deployed SW is sitting in the "waiting" state.
//  • "Reload" → updateServiceWorker(true) postMessages SKIP_WAITING and reloads on
//    `controllerchange`. iOS standalone (added-to-home-screen) PWAs often DON'T fire
//    controllerchange, so a forced reload fallback guarantees the home-screen app
//    actually picks up the new build.
//  • "Later" hides the banner; the update stays staged for the next launch.
export default function ReloadPrompt() {
  const [reloading, setReloading] = useState(false);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      const check = () => registration.update().catch(() => {});
      check();                                 // right after registering
      setInterval(check, 30 * 60 * 1000);      // every 30 min while open
      // …and whenever the installed app returns to the foreground.
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
      window.addEventListener('focus', check);
    },
  });

  const doReload = async () => {
    setReloading(true);
    try { await updateServiceWorker(true); } catch { /* ignore */ }
    // iOS standalone fallback: force the reload if controllerchange never fires.
    setTimeout(() => window.location.reload(), 2000);
  };

  if (reloading) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 5000, background: 'color-mix(in srgb,var(--bg) 82%,transparent)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14 }}>
        <span style={{ width: 30, height: 30, border: '3px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Updating…</span>
      </div>
    );
  }
  if (!needRefresh) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed', left: 12, right: 12,
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
        zIndex: 4000, display: 'flex', alignItems: 'center', gap: 12,
        borderRadius: 14, padding: '12px 12px 12px 16px',
        background: '#241854', color: '#fff',
        boxShadow: '0 10px 30px rgba(10,15,30,.35)',
        maxWidth: 436, margin: '0 auto',
      }}
    >
      <span style={{ flex: 1, fontSize: 13, fontWeight: 500, lineHeight: 1.35 }}>
        A new version of the app is available.
      </span>
      <button
        type="button"
        onClick={() => setNeedRefresh(false)}
        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.65)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '8px 6px' }}
      >
        Later
      </button>
      <button
        type="button"
        onClick={doReload}
        style={{ background: 'var(--accent, #2E7CC4)', border: 'none', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', padding: '8px 16px', borderRadius: 9 }}
      >
        Reload
      </button>
    </div>
  );
}
